import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, PackageCheck, FileSearch, FlaskConical, ArrowRight } from "lucide-react";
import { useSEO, breadcrumbSchema } from "@/lib/seo";
import PageHeader from "@/components/PageHeader";
import { Reveal, StaggerGroup, StaggerItem, GoldRule } from "@/components/motion/Reveal";

const STANDARDS = [
  { icon: FileSearch, title: "Organized Information", body: "Every material is catalogued with clear identification, format, and presentation so records and physical inventory stay aligned." },
  { icon: PackageCheck, title: "Careful Handling & Packaging", body: "Orders are prepared with discreet, protective packaging appropriate for research materials, handled with consistency at every step." },
  { icon: ShieldCheck, title: "Transparency", body: "We present product information plainly and publish genuine supporting documents when they are supplied — never fabricated stand-ins." },
  { icon: FlaskConical, title: "Research-Only Positioning", body: "Our materials are offered strictly for laboratory research and analytical use, and are presented as such throughout." },
];

export default function QualityStandards() {
  useSEO({
    title: "Quality Standards | Renova Peptide Co.",
    description:
      "Learn how Renova Peptide Co. approaches organized product information, careful handling, transparency, and research-only positioning.",
    path: "/quality-standards",
    jsonLd: breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Quality Standards", path: "/quality-standards" }]),
  });

  return (
    <>
      <PageHeader
        overline="Our Approach"
        title="Quality Standards"
        intro="Renova is built around clarity and careful presentation. These are the principles that guide how we catalogue, handle, and present our research materials."
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Quality Standards", path: "/quality-standards" }]}
      />
      <section className="container-rnv py-16 md:py-24">
        <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {STANDARDS.map(({ icon: Icon, title, body }) => (
            <StaggerItem key={title}>
              <div className="h-full bg-white rounded-sm border border-[#1E2A44]/8 p-8 md:p-10">
                <span className="w-12 h-12 rounded-sm border border-[#C8A96A]/60 flex items-center justify-center text-[#C8A96A]">
                  <Icon size={22} strokeWidth={1.5} />
                </span>
                <h2 className="font-serif text-2xl text-[#1E2A44] mt-6">{title}</h2>
                <p className="mt-3 text-sm text-[#85827D] leading-relaxed">{body}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>

        <GoldRule className="my-16" />

        <Reveal>
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl text-[#1E2A44]">A note on claims</h2>
            <p className="mt-4 text-[#262E3D] leading-relaxed">
              We are careful not to overstate. Where verified documentation is available for a
              specific material or lot, it is published in our COAs / Testing section. Where it is
              not yet available, we say so plainly rather than inventing figures or certifications.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/coas" className="btn-navy">View Documentation <ArrowRight size={16} /></Link>
              <Link to="/shop" className="btn-gold">Browse Products</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
